import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { WatermarkSummary } from 'fym-shared/src/music24/watermarkUpsell';
import { TypedMemo } from '../../../common/types';
import { WatermarkThumbTile } from '../files/WatermarkThumbTile';
import { watermarkTrialMessages as messages } from '../trial/watermarkTrialMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TrialTakeoverFilesProps {
  kind: 'lastDay' | 'free';
  summary: WatermarkSummary;
  /**
   * The workspace never had a trial (`useNeverHadTrial`): the Free copy says nothing about a change
   * ("now"), because nothing changed for this person.
   */
  neverHadTrial?: boolean;
}

/**
 * The left column of the takeover (design: trial-upsell b3): the headline with the count of the
 * user's files, their newest thumbnails with "+N", and what Free keeps.
 */
function eyebrowMessage(kind: TrialTakeoverFilesProps['kind'], isNoTrialFree: boolean) {
  if (kind === 'lastDay') {
    return messages.endsTomorrow;
  }
  return isNoTrialFree ? messages.onFree : messages.onFreeNow;
}

const TrialTakeoverFilesComponent: React.FC<TrialTakeoverFilesProps> = ({ kind, summary, neverHadTrial = false }) => {
  const intl = useIntl();
  const count = summary.files;
  const more = count - summary.thumbs.length;
  const isNoTrialFree = kind === 'free' && neverHadTrial;
  let title = intl.formatMessage(isNoTrialFree ? messages.takeoverTitleNoTrial : messages.takeoverTitleFree, { count });
  if (kind === 'lastDay') {
    title =
      summary.songTitle === null
        ? intl.formatMessage(messages.takeoverTitleLastDayNoSong, { count })
        : intl.formatMessage(messages.takeoverTitleLastDay, { count, song: summary.songTitle });
  }
  return (
    <div className={styles.col}>
      <div className={clsx(styles.eyebrow, styles.eyebrowWarm)}>
        {intl.formatMessage(eyebrowMessage(kind, isNoTrialFree))}
      </div>
      <h3 className={clsx(styles.h, styles.hBig)} data-testid="trial-takeover-title">
        {withBrandWord(title)}
      </h3>
      <div className={styles.grid} aria-hidden="true">
        {summary.thumbs.map((thumb) => (
          <WatermarkThumbTile key={thumb.url} url={thumb.url} shape={thumb.shape} />
        ))}
        {more > 0 && <div className={styles.tileMore}>+{more}</div>}
      </div>
      <p className={styles.fine}>{intl.formatMessage(messages.takeoverFine)}</p>
    </div>
  );
};

export const TrialTakeoverFiles = TypedMemo(TrialTakeoverFilesComponent);
